import { LangBadges } from "./LanguagePicker";
import { Button } from "./ui/button";
import { useI18n } from "../context/I18nContext";
import { MapPin, UserPlus, Check, Clock, Sparkles, Phone } from "lucide-react";

function StatusDot({ u }) {
  if (!u.online) return <span className="w-3 h-3 rounded-full bg-muted-foreground/40 border-2 border-card" title="Offline" />;
  if (u.status === "in_call") return <span className="w-3 h-3 rounded-full bg-primary border-2 border-card" title="In a call" />;
  return <span className="w-3 h-3 rounded-full bg-secondary border-2 border-card" title="Online" />;
}

export default function UserCard({ u, actionState, onAction, onCall, testid, showStatus = false }) {
  const { t } = useI18n();
  const initials = (u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <div className="rounded-3xl bg-card border border-border shadow-sm p-6 transition-all hover:-translate-y-1 hover:shadow-md hover:border-primary/40" data-testid={testid}>
      <div className="flex items-center gap-4">
        <div className="relative">
          <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center font-heading font-bold text-lg text-primary shrink-0">
            {initials}
          </div>
          {showStatus && <span className="absolute -bottom-0.5 -right-0.5" data-testid={`${testid}-status`}><StatusDot u={u} /></span>}
        </div>
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-lg truncate">{u.name}</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {u.country && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {u.country}</span>}
            {showStatus && u.status === "in_call" && <span className="text-primary font-medium">{t("card.inCall")}</span>}
          </div>
        </div>
        <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-secondary bg-secondary/15 rounded-full px-2.5 py-1 shrink-0">
          <Sparkles className="w-3 h-3" /> {u.points ?? 0}
        </span>
      </div>
      {u.bio && <p className="text-sm text-foreground/70 mt-4 line-clamp-2">{u.bio}</p>}
      <div className="mt-4 space-y-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{t("card.speaks")}</p>
          <LangBadges langs={u.native_languages} variant="secondary" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{t("card.learning")} · {u.tier || "Beginner"}</p>
          <LangBadges langs={u.learning_languages} variant="primary" />
        </div>
      </div>
      {onCall ? (
        <div className="mt-5">
          {u.status === "in_call" ? (
            <Button disabled variant="outline" className="w-full rounded-full" data-testid={`${testid}-incall`}>
              <Phone className="w-4 h-4 mr-1" /> {t("card.inCallBtn")}
            </Button>
          ) : u.online ? (
            <Button onClick={() => onCall(u)} className="w-full rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid={`${testid}-call`}>
              <Phone className="w-4 h-4 mr-1" /> {t("card.call")}
            </Button>
          ) : (
            <Button disabled variant="outline" className="w-full rounded-full opacity-60" data-testid={`${testid}-offline`}>
              {t("card.offlineBtn")}
            </Button>
          )}
        </div>
      ) : onAction && (
        <div className="mt-5">
          {actionState === "friend" ? (
            <Button disabled variant="secondary" className="w-full rounded-full" data-testid={`${testid}-friend`}>
              <Check className="w-4 h-4 mr-1" /> {t("card.friends")}
            </Button>
          ) : actionState === "pending" ? (
            <Button disabled variant="outline" className="w-full rounded-full" data-testid={`${testid}-pending`}>
              <Clock className="w-4 h-4 mr-1" /> {t("card.requested")}
            </Button>
          ) : (
            <Button onClick={() => onAction(u)} className="w-full rounded-full" data-testid={`${testid}-add`}>
              <UserPlus className="w-4 h-4 mr-1" /> {t("card.addFriend")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
